import PropTypes from 'prop-types';
import React, { useState, useRef, useEffect } from 'react';
import usePrevious from 'use-previous';
import { FormattedMessage, injectIntl } from 'react-intl';
import ImmutablePropTypes from 'react-immutable-proptypes';
import Immutable from 'immutable';

import { Select, SelectOption } from '@bibliocommons/select';
import { setFocusWithinElement } from '@bibliocommons/utils-accessibility';
import { TextButton, SecondaryTextButton, TransactionalSolidButton } from '@bibliocommons/base-buttons';
import { ugcAgeSuitabilityShape, catalogBibShape } from '@bibliocommons/bc-prop-types';
import DeleteAgeRatingModalContainer from 'app/components/bibs/shared/ugc/DeleteAgeRatingModal';

import './AgeRatingEditor.scss';

const MAX_AGE = 21;
const EDITOR_KEY = 'ageRating';

export function AgeRatingEditor({
  handleSuccess,
  currentUserAgeSuitability,
  metadataId,
  open,
  catalogBib,
  ageSuitabilitiesTransactions,
  ageSuitabilitiesActions,
  modalActions,
  ugcEditorActions,
  intl
}) {
  const hasUserRating = currentUserAgeSuitability?.size;
  const rating = hasUserRating ? currentUserAgeSuitability.get('min') : null;
  const [ageRating, setAgeRating] = useState(rating);
  const ageSuitabilityId = currentUserAgeSuitability?.get('id');
  const isLoading =
    ageSuitabilitiesTransactions.getIn([metadataId, 'isAdding']) ||
    ageSuitabilitiesTransactions.getIn([metadataId, ageSuitabilityId, 'isUpdating']);

  const prevOpen = usePrevious(open);
  const editButtonRef = useRef();
  const selectRef = useRef();

  useEffect(() => {
    // Set focus on the dropdown when the form opens
    if (open && !prevOpen && selectRef.current) {
      setFocusWithinElement(selectRef.current);
    }

    // Set focus on the edit button when the form is closed
    if (!open && prevOpen && editButtonRef.current) {
      setFocusWithinElement(editButtonRef.current);
    }
  }, [open, prevOpen, selectRef, editButtonRef]);

  function handleSubmit(e) {
    const min = ageRating > 0 ? ageRating : null;
    e?.preventDefault(); // eslint-disable-line babel/no-unused-expressions

    if (ageSuitabilityId) {
      ageSuitabilitiesActions.updateAgeSuitability({
        min,
        metadataId,
        ageSuitabilityId,
        postSuccess: () => {
          ugcEditorActions.closeEditor(EDITOR_KEY);
          if (handleSuccess) handleSuccess();
        }
      });
    } else {
      ageSuitabilitiesActions.addAgeSuitability({
        metadataId,
        min,
        postSuccess: () => {
          ugcEditorActions.closeEditor(EDITOR_KEY);
          if (handleSuccess) handleSuccess();
        }
      });
    }
  }

  function getAgeOptions() {
    const ageOpts = Array(MAX_AGE + 1)
      .fill(null)
      .map((_, age) => ({
        key: `age-rating-${age}`,
        value: age,
        label: age === 0 ? intl.formatMessage({ id: 'ugc_age_suitability_age_all' }) : `${age}+`
      }));

    return Immutable.fromJS(ageOpts);
  }

  function renderSelect() {
    const ageOptions = getAgeOptions();
    const renderOption = age => (
      <SelectOption key={age.get('key')} value={age.get('value')}>
        {age.get('label')}
      </SelectOption>
    );

    return (
      <Select
        id="rating-select"
        dataKey="rating-select"
        name="age"
        value={ageRating}
        ref={selectRef}
        handleChange={setAgeRating}
        prompt={intl.formatMessage({
          id: 'age_suitability_select'
        })}
      >
        {ageOptions.map(renderOption)}
      </Select>
    );
  }

  function renderEditorOpen() {
    return (
      <>
        {renderSelect()}
        <div className="editor-buttons">
          <TransactionalSolidButton
            disabled={ageRating === null}
            dataKey="submit-button"
            className="age-rating-submit-button"
            handleClick={handleSubmit}
            isLoading={isLoading}
          >
            <FormattedMessage id="post_age_rating" />
          </TransactionalSolidButton>
          <SecondaryTextButton
            handleClick={() => ugcEditorActions.closeEditor(EDITOR_KEY)}
            className="age-rating-cancel-button"
            dataKey="cancel-button"
          >
            <FormattedMessage id="button_cancel" />
          </SecondaryTextButton>
        </div>
      </>
    );
  }

  function renderEditorClosed() {
    return (
      <>
        {hasUserRating ? (
          <span className="rating-label">
            <FormattedMessage id="your_rating" />
          </span>
        ) : null}
        <TextButton
          ref={editButtonRef}
          handleClick={() => ugcEditorActions.openEditor(EDITOR_KEY)}
          className="open-editor-button"
          dataKey="open-editor-button"
          size="large"
        >
          <FormattedMessage id={currentUserAgeSuitability?.size ? 'edit_button' : 'age_suitability_add_rating'} />
        </TextButton>
        {currentUserAgeSuitability && (
          <TextButton
            handleClick={() => modalActions.openModal(`delete-age-rating-${ageSuitabilityId}`)}
            className="delete-rating-button"
            dataKey="delete-rating-button"
            size="large"
          >
            <FormattedMessage id="delete_button" />
          </TextButton>
        )}
      </>
    );
  }

  return (
    <div className="age-rating-editor">
      {open ? renderEditorOpen() : renderEditorClosed()}
      {ageSuitabilityId && (
        <DeleteAgeRatingModalContainer
          handleSuccess={handleSuccess}
          ageSuitabilityId={ageSuitabilityId}
          catalogBib={catalogBib}
        />
      )}
    </div>
  );
}

AgeRatingEditor.propTypes = {
  handleSuccess: PropTypes.func.isRequired,
  currentUserAgeSuitability: ugcAgeSuitabilityShape,
  metadataId: PropTypes.string,
  open: PropTypes.bool,
  catalogBib: catalogBibShape.isRequired,
  ageSuitabilitiesTransactions: ImmutablePropTypes.map.isRequired,
  ageSuitabilitiesActions: PropTypes.objectOf(PropTypes.func).isRequired,
  modalActions: PropTypes.objectOf(PropTypes.func).isRequired,
  ugcEditorActions: PropTypes.objectOf(PropTypes.func).isRequired
};

export default injectIntl(AgeRatingEditor);
